import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import EastIcon from '@mui/icons-material/East';
import WestIcon from '@mui/icons-material/West';
import { useNavigate, useParams } from 'react-router-dom';
import StepFourChecklistPanel from '../components/StepFourChecklistPanel';
import { useAuth } from '../auth/AuthContext';
import { isLoadTestingProject } from '../utils/testingType';
import ProjectLlmChatWidget from '../components/ProjectLlmChatWidget';

export default function ProjectChecklistPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiClient } = useAuth();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qaRefresh, setQaRefresh] = useState(0);

  const loadProject = useCallback(async () => {
    if (!id) {
      setError('Не указан проект.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/api/projects/${id}`);
      setProject(response?.data || null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Не удалось загрузить проект.');
    } finally {
      setLoading(false);
    }
  }, [apiClient, id]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const isLoadProject = isLoadTestingProject(project);

  return (
    <Stack spacing={3} className="stagger-children">
      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : isLoadProject ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Alert severity="info">
              Шаг 4 (Checklist) недоступен для проекта нагрузочного тестирования.
            </Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="outlined" startIcon={<WestIcon />} onClick={() => navigate(`/projects/${id}`)}>
                К шагу 1
              </Button>
              <Button variant="contained" endIcon={<EastIcon />} onClick={() => navigate(`/projects/${id}/test-cases`)}>
                К шагу 2 (нагрузочные тест-кейсы)
              </Button>
            </Stack>
          </Stack>
        </Paper>
      ) : (
        <>
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Typography variant="h4">
                Шаг 4. Checklist (из шага 3)
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Chip label={`Проект: ${project?.name || '—'}`} />
                <Chip label={`Требования: ${project?.requirementsCount || 0}`} />
              </Stack>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button
                variant="outlined"
                startIcon={<WestIcon />}
                onClick={() => navigate(`/projects/${id}/test-cases`)}
              >
                К шагу 3
              </Button>
              <Button
                variant="outlined"
                endIcon={<EastIcon />}
                onClick={() => navigate(`/projects/${id}/summary-report`)}
              >
                К шагу 5
              </Button>
            </Stack>
          </Stack>

          <Divider />

          <StepFourChecklistPanel
            projectId={id}
            apiClient={apiClient}
            refreshSignal={qaRefresh}
            disabled={(project?.requirementsCount || 0) === 0}
            disabledReason="Сначала сформируйте тест-кейсы на шаге 3."
          />
        </>
      )}
      {project && <ProjectLlmChatWidget project={project} stage="checklist" onQaArtifactChanged={() => setQaRefresh((value) => value + 1)} />}
    </Stack>
  );
}
